import { memo } from 'react';
import { StatusIndicator } from '@amzn/awsui-components-core';
import { useContentEditorPageViewModel } from './ContentEditorPageViewModel';
import ContentLoadingErrorPage from '../v2-common/ContentLoadingErrorPage';
import FixedWidthContentContainer from '../v2-common/FixedWidthContentContainer';
import NotFound from '../common/NotFound';
import LoadedContentEditor from './LoadedContentEditor';
import { LoadedContentView } from '../v2-content-view/LoadedContentView';
import ActionPanel from '../v2-content-action-panel/ActionPanel';
import QualityCheckModal from './QualityCheckModal';
import EditForbiddenModal from './EditForbiddenModal';
import { useLegacyWorkflowRedirect } from '../v2-hooks/useLegacyWorkflowRedirect';
import styles from './ContentEditorPage.module.scss';

/**
 * Full page that renders an APGL content item for editing.
 * Shows a loading indicator, a not-found page (for missing or unauthorized
 * content), the content read-only behind a redirect modal (when the user may
 * not edit it), an error alert, or the loaded content editor depending on the
 * view model status.
 *
 * Layout: Uses ActionPanel which wraps Cloudscape PanelLayout with
 * main content on the left and a collapsible action panel on the right.
 */
const ContentEditorPage = memo(function ContentEditorPage() {
  const vm = useContentEditorPageViewModel();

  const isRedirectingToLegacyPage = useLegacyWorkflowRedirect();

  // A missing or forbidden content renders the 404 page rather than exposing
  // the editor chrome around an empty body.
  if (vm.status === 'notFound') return <NotFound />;

  // Peer-review stages are deprecated — redirect to v1 which still handles them.
  if (isRedirectingToLegacyPage) return null;

  // Not allowed to edit: render the loaded document with the read-only view
  // (no editable controls) and the non-dismissible redirect modal on top, so
  // the user sees the content behind the modal rather than a blank page. The
  // read-only surface reads the same already-loaded ContentEditorService.
  if (vm.status === 'editForbidden') {
    return (
      <>
        <FixedWidthContentContainer>
          <div className={styles.container}>
            <LoadedContentView showStatusBanners={false} />
          </div>
        </FixedWidthContentContainer>
        <EditForbiddenModal
          reason={vm.reason}
          contentId={vm.contentId}
          lockHolderAlias={vm.lockHolderAlias}
        />
      </>
    );
  }

  return (
    <>
      <FixedWidthContentContainer>
        <div className={styles.container}>
          {vm.status === 'loading' && (
            <StatusIndicator type="loading">Loading content</StatusIndicator>
          )}
          {vm.status === 'error' && <ContentLoadingErrorPage message={vm.errorMessage} />}
          {vm.status === 'loaded' && <LoadedContentEditor key={vm.editorRemountKey} vm={vm} />}
        </div>
      </FixedWidthContentContainer>
      <QualityCheckModal />
    </>
  );
});

export default ContentEditorPage;
